import axios, { type AxiosInstance } from "axios"
import { getExhibitionHeader } from "../utils/exhibitionContext";

export interface Country {
  id: number
  name: string
  code: string
  continent?: number
}

export interface Exhibitors {
  id: number
  name: string
  tin: number
  email: string
  phone: number
  type: number
  country: number
  country_name?: string
  status: boolean
  postpaid: boolean
  is_host: boolean
  postpaid_document?: string | null
  type_name?: string // Optional field to hold type name
}

const api: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://127.0.0.1:8000",
  headers: {
    "Content-Type": "application/json",
  },
})

// ✅ Dynamically attach token before each request
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("accessToken")
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`
    Object.assign(config.headers, getExhibitionHeader());
    }
    return config
  },
  (error) => Promise.reject(error),
)

interface PaginatedResponse<T> {
  count: number
  next: string | null
  previous: string | null
  total_pages: number
  current_page: number
  page_size: number
  results: T[]
}

interface GetAllParams {
  page?: number
  page_size?: number
  search?: string
}



const ExhibitorsService = {
  getAll: async (params: GetAllParams = {}): Promise<PaginatedResponse<Exhibitors>> => {
    const { page = 1, page_size = 10, search = "" } = params

    const queryParams = new URLSearchParams({
      page: page.toString(),
      page_size: page_size.toString(),
    })

    if (search.trim()) {
      queryParams.append("search", search.trim())
    }

    const response = await api.get(`/api/stakeholders/exhibitors/?${queryParams.toString()}`)
    return response.data
  },

  getById: async (id: number): Promise<Exhibitors> => {
    const response = await api.get(`/api/stakeholders/exhibitor/${id}/`)
    return response.data
  },

  
  // Method to get all countries without pagination (for initial load)
  getAllCountries: async (): Promise<Country[]> => {
    const response = await api.get("/api/core/countries/all/")
    return response.data
  },

  async create(data: FormData): Promise<Exhibitors> {
    const response = await api.post("/api/stakeholders/exhibitors/create/", data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    return response.data
  },

  async update(id: number, data: FormData): Promise<Exhibitors> {
    const response = await api.put(`/api/stakeholders/exhibitors/${id}/update/`, data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    return response.data
  },

  async delete(id: number): Promise<void> {
    await api.delete(`/api/stakeholders/exhibitors/${id}/delete/`)
  },

  async viewDocument(documentUrl: string): Promise<void> {
    // The backend should already return the full URL
    // If not, construct it properly
    let fullUrl = documentUrl
    if (!documentUrl.startsWith("http")) {
      // Remove leading slash if present and construct full URL
      const cleanPath = documentUrl.startsWith("/") ? documentUrl.slice(1) : documentUrl
      const baseURL = process.env.REACT_APP_API_URL || "http://localhost:8000"
      fullUrl = `${baseURL}/${cleanPath}`
    }
    console.log("Opening document URL:", fullUrl) // Debug log
    window.open(fullUrl, "_blank")
  },
}

export default ExhibitorsService
